import { AuthUser } from "../store/userSlice";
import { AccessType } from "./userApi";

const API_URL = `${import.meta.env.VITE_API_URL || "http://localhost:5000/api"}/auth`;

// Interface pour les données de connexion
export interface LoginCredentials {
  email: string;
  password: string;
}

// Interface pour la réponse d'authentification
export interface AuthResponse {
  user: {
    _id: string;
    firstName: string;
    lastName: string;
    companyName?: string;
    microsoftId?: string;
    departmentId?: string;
    departmentFlags?: {
      isStore?: boolean;
      isSupplier?: boolean;
      isAccountManagement?: boolean;
    } | null;
    function?: string;
    jobFunction?: string;
    shopLocationId?: string;
    accesses: string[];
    level?: 'employee' | 'manager1' | 'manager2';
    reportingScopeType?: 'all' | 'supplier' | 'brands';
    reportingSupplierId?: string | null;
    reportingBrands?: string[];
  };
  token: string;
}

/**
 * Authentifie un utilisateur avec ses identifiants
 * @param credentials Identifiants (email et mot de passe)
 * @returns Données utilisateur et token d'authentification
 */
export const loginUser = async (
  credentials: LoginCredentials
): Promise<AuthUser> => {
  try {
    const response = await fetch(`${API_URL}/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(credentials),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Échec de connexion");
    }

    const data: AuthResponse = await response.json();

    // Débugger le contenu des accès reçus
    console.log("Accès reçus du backend:", data.user.accesses);

    // Vérifier si jobFunction est présent sinon utiliser function
    const userFunction = data.user.jobFunction || data.user.function || '';

    // Transformer la réponse API en objet AuthUser
    return {
      id: data.user._id,
      firstName: data.user.firstName,
      lastName: data.user.lastName,
      companyName: data.user.companyName,
      microsoftId: data.user.microsoftId,
      departmentId: data.user.departmentId,
      departmentFlags: data.user.departmentFlags,
      function: userFunction,
      shopLocationId: data.user.shopLocationId, // Ajout de l'ID d'emplacement Shopify
      accesses: data.user.accesses as AccessType[],
      level: data.user.level ?? 'employee',
      token: data.token,
      reportingScopeType: data.user.reportingScopeType,
      reportingSupplierId: data.user.reportingSupplierId,
      reportingBrands: data.user.reportingBrands,
    };
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(error.message);
    }
    throw new Error("Erreur inconnue lors de la connexion");
  }
};

/**
 * Vérifie si un token est valide
 * @param token Token d'authentification à vérifier
 * @returns Données utilisateur mises à jour si le token est valide
 */
export const verifyToken = async (token: string): Promise<AuthUser> => {
  try {
    const response = await fetch(`${API_URL}/verify`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error("Token invalide");
    }

    const data: AuthResponse = await response.json();

    const userFunction = data.user.jobFunction || data.user.function || '';
    return {
      id: data.user._id,
      firstName: data.user.firstName,
      lastName: data.user.lastName,
      companyName: data.user.companyName,
      microsoftId: data.user.microsoftId,
      departmentId: data.user.departmentId,
      departmentFlags: data.user.departmentFlags,
      function: userFunction,
      shopLocationId: data.user.shopLocationId,
      accesses: data.user.accesses as AccessType[],
      level: data.user.level ?? 'employee',
      token: data.token,
      reportingScopeType: data.user.reportingScopeType,
      reportingSupplierId: data.user.reportingSupplierId,
      reportingBrands: data.user.reportingBrands,
    };
  } catch {
    throw new Error("Session expirée");
  }
};
